/**
 * ANALYTICS - Communication avec le site parent via postMessage
 *
 * Les formulaires publics Desertours sont intégrés en iframe sur 4LTROPHY.
 * Comme les domaines sont différents, on ne peut pas accéder directement
 * aux fonctions de tracking (fbq, gtag) du parent.
 *
 * Solution : postMessage pour notifier le parent des conversions.
 * Le site 4LTROPHY écoute ces messages et déclenche son propre tracking.
 */

/** Types de messages envoyés au parent */
export type ParentMessageType =
  | 'DESERTOURS_FORM_STARTED'
  | 'DESERTOURS_LEAD'
  | 'DESERTOURS_PAYMENT_READY'
  | 'DESERTOURS_BED_STATE'
  | 'DESERTOURS_BED_SCROLL_TOP'
  | 'DESERTOURS_FORM_READY'
  | 'DESERTOURS_FORM_HEIGHT';

/** Payload pour une conversion Lead */
export interface LeadConversionPayload {
  formName: string;
  productName?: string;
  timestamp: string;
  /**
   * PII du lead (pilote) en clair, pour advanced matching côté parent.
   * Le site parent (4LTROPHY) hashe en SHA-256 côté serveur avant envoi Meta CAPI / GA4 MP.
   * Le CRM ne fait AUCUN hash ni appel réseau tracking.
   */
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  country?: string;
}

/** Payload pour un début de saisie sur le formulaire */
export interface FormStartedPayload {
  formName: string;
  productName?: string;
  timestamp: string;
}

/** Structure d'un message postMessage FormStarted */
export interface FormStartedMessage {
  type: 'DESERTOURS_FORM_STARTED';
  payload: FormStartedPayload;
}

/** Structure d'un message postMessage Lead */
export interface ParentMessage {
  type: 'DESERTOURS_LEAD';
  payload: LeadConversionPayload;
}

/** Structure d'un message postMessage Payment Ready */
export interface PaymentReadyMessage {
  type: 'DESERTOURS_PAYMENT_READY';
  payment_case_id: string;
  product: string;
  payment_type: 'BPI' | 'BED';
  /**
   * Lien de paiement BED (bed_tokens.token) — présent uniquement pour
   * payment_type='BED'. Le site parent (exec 2) l'utilise pour ouvrir le tunnel
   * de paiement du solde sur le bon dossier. Non sensible (jeton à usage unique,
   * TTL 30 j). Absent pour le BPI.
   */
  bed_token?: string;
  /** order_id du dossier BED (contrat multi-CB `…-BED`). BED uniquement. */
  order_id?: string;
}

/**
 * États du parcours BED remontés au parent (vague 1).
 * `payment_ready` COEXISTE avec `DESERTOURS_PAYMENT_READY` (rétro-compat :
 * l'ancien message reste émis à l'identique, ne pas le supprimer).
 */
export type BedStateName =
  | 'loaded'
  | 'step_saved'
  | 'submitted_complete'
  | 'submitted_incomplete'
  | 'payment_ready';

/**
 * Élément manquant d'un BED, tel que calculé par le serveur
 * (`bed_compute_missing_items`, migration 00231).
 */
export interface BedMissingItemPayload {
  /** `field:<uuid>` (champ requis vide) ou `<groupe>:<rôle>` (report déclaré). */
  code: string;
  kind: 'deferred' | 'empty';
  label: string;
  role: string;
  role_label: string;
  /** Date d'engagement, uniquement pour `kind: 'deferred'`. */
  due_date?: string | null;
}

/**
 * Structure d'un message postMessage BED_STATE.
 * Contrat documenté dans `docs/BED_IFRAME_EVENTS.md`.
 */
export interface BedStateMessage {
  type: 'DESERTOURS_BED_STATE';
  state: BedStateName;
  /** Étape 1-6 concernée (chargement / sauvegarde d'étape). */
  step?: number;
  /** Numéro d'équipage, pour que le parent rattache l'événement sans PII. */
  crew_number?: string | null;
  missing_items?: BedMissingItemPayload[];
  payment_case_id?: string;
  bed_token?: string;
  order_id?: string;
  /** Montant que le participant a choisi de régler maintenant (centimes). */
  amount_to_pay_cents?: number | null;
  /**
   * Statut CRM du dossier APRÈS la transition (migration 00232) :
   * `pre_registered` | `bed_downloaded` | `bed_incomplete` |
   * `bed_incomplete_payment` | `bed_complete` | `completed`.
   * Toujours calculé côté serveur — le client ne fait que le relayer.
   * Porté par `loaded`, `submitted_*` et `payment_ready`.
   */
  bed_status?: string | null;
  timestamp: string;
}

/** Structure d'un message de redimensionnement du formulaire embarqué */
export interface FormHeightMessage {
  type: 'DESERTOURS_FORM_HEIGHT';
  height: number;
}

/**
 * Vérifie si on est en contexte iframe
 */
function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Si erreur cross-origin, on est probablement dans une iframe
    return true;
  }
}

/**
 * Récupère le targetOrigin depuis la variable d'environnement.
 * Jamais de wildcard '*' — sécurité postMessage.
 */
function getTargetOrigin(): string | null {
  const origin = import.meta.env.VITE_PARENT_ORIGIN;
  if (!origin) {
    if (isInIframe()) {
      console.error(
        '[Analytics] CRITIQUE : VITE_PARENT_ORIGIN non défini en contexte iframe. ' +
          'Le postMessage est désactivé — le tunnel de paiement ne fonctionnera pas. ' +
          "Ajouter VITE_PARENT_ORIGIN dans les variables d'environnement."
      );
    }
    return null;
  }
  return origin;
}

/**
 * Notifie le site parent (4LTROPHY) du début de saisie sur un formulaire.
 * Émis une seule fois, au premier champ rempli — sans PII.
 */
export function notifyParentFormStarted(params: { formName: string; productName?: string }): void {
  if (!isInIframe()) {
    // eslint-disable-next-line no-console -- trace analytics legit pour debug GA4 cross-origin
    console.debug('[Analytics] Pas en iframe, postMessage FORM_STARTED ignoré');
    return;
  }

  try {
    const message: FormStartedMessage = {
      type: 'DESERTOURS_FORM_STARTED',
      payload: {
        formName: params.formName,
        productName: params.productName,
        timestamp: new Date().toISOString(),
      },
    };

    const targetOrigin = getTargetOrigin();
    if (!targetOrigin) return;

    window.parent.postMessage(message, targetOrigin);
    // eslint-disable-next-line no-console -- trace analytics legit pour debug GA4 cross-origin
    console.log('[Analytics] postMessage DESERTOURS_FORM_STARTED envoyé', {
      formName: message.payload.formName,
      productName: message.payload.productName,
      timestamp: message.payload.timestamp,
    });
  } catch (error) {
    console.warn('[Analytics] Erreur postMessage FORM_STARTED:', error);
  }
}

/**
 * Notifie le site parent (4LTROPHY) d'une conversion Lead
 * Utilise postMessage pour contourner la restriction cross-origin
 *
 * @param params - Informations sur le formulaire soumis
 */
export function notifyParentLeadConversion(params: {
  formName: string;
  productName?: string;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  country?: string;
}): void {
  // Ne rien faire si on n'est pas dans une iframe
  if (!isInIframe()) {
    // eslint-disable-next-line no-console -- trace analytics legit pour debug GA4 cross-origin
    console.debug('[Analytics] Pas en iframe, postMessage ignoré');
    return;
  }

  try {
    const message: ParentMessage = {
      type: 'DESERTOURS_LEAD',
      payload: {
        formName: params.formName,
        productName: params.productName,
        timestamp: new Date().toISOString(),
        email: params.email,
        phone: params.phone,
        firstName: params.firstName,
        lastName: params.lastName,
        country: params.country,
      },
    };

    // Validation targetOrigin via VITE_PARENT_ORIGIN (sécurité postMessage)
    const targetOrigin = getTargetOrigin();
    if (!targetOrigin) return;

    window.parent.postMessage(message, targetOrigin);
    // On ne log JAMAIS la PII en clair : uniquement des booléens de présence.
    // eslint-disable-next-line no-console -- trace analytics legit pour debug GA4 cross-origin
    console.log('[Analytics] postMessage DESERTOURS_LEAD envoyé', {
      formName: message.payload.formName,
      productName: message.payload.productName,
      timestamp: message.payload.timestamp,
      has_email: !!message.payload.email,
      has_phone: !!message.payload.phone,
      has_first_name: !!message.payload.firstName,
      has_last_name: !!message.payload.lastName,
      has_country: !!message.payload.country,
    });
  } catch (error) {
    console.warn('[Analytics] Erreur postMessage:', error);
  }
}

/**
 * Notifie le site parent qu'un payment_case est prêt pour la redirection.
 * Émis uniquement si generates_bpi === true ET contexte iframe.
 * Aucune donnée sensible (pas de montant, pas d'email).
 */
export function notifyParentPaymentReady(params: { paymentCaseId: string; product: string }): void {
  if (!isInIframe()) {
    // eslint-disable-next-line no-console
    console.debug('[Analytics] Pas en iframe, postMessage PAYMENT_READY ignoré');
    return;
  }

  try {
    const message: PaymentReadyMessage = {
      type: 'DESERTOURS_PAYMENT_READY',
      payment_case_id: params.paymentCaseId,
      product: params.product,
      payment_type: 'BPI',
    };

    const targetOrigin = getTargetOrigin();
    if (!targetOrigin) return;

    window.parent.postMessage(message, targetOrigin);
    // eslint-disable-next-line no-console
    console.log('[Analytics] postMessage DESERTOURS_PAYMENT_READY envoyé', {
      payment_case_id: params.paymentCaseId,
      product: params.product,
    });
  } catch (error) {
    console.warn('[Analytics] Erreur postMessage PAYMENT_READY:', error);
  }
}

/**
 * Notifie le site parent qu'un dossier de paiement BED (solde) est prêt.
 * Émis uniquement si generates_bed === true ET contexte iframe.
 *
 * CONTRAT postMessage BED (à consommer côté site — exec 2) :
 *   { type: 'DESERTOURS_PAYMENT_READY', payment_type: 'BED',
 *     payment_case_id, product, bed_token, order_id }
 * Le site ouvre le tunnel de paiement du solde en résolvant le dossier via
 * `bed_token` (lien de paiement usage unique, TTL 30 j). Aucune donnée
 * financière (montant) n'est transmise : le site relit le solde serveur-side.
 */
export function notifyParentBedPaymentReady(params: {
  paymentCaseId: string;
  product: string;
  bedToken: string;
  orderId: string;
}): void {
  if (!isInIframe()) {
    // eslint-disable-next-line no-console
    console.debug('[Analytics] Pas en iframe, postMessage BED PAYMENT_READY ignoré');
    return;
  }

  try {
    const message: PaymentReadyMessage = {
      type: 'DESERTOURS_PAYMENT_READY',
      payment_case_id: params.paymentCaseId,
      product: params.product,
      payment_type: 'BED',
      bed_token: params.bedToken,
      order_id: params.orderId,
    };

    const targetOrigin = getTargetOrigin();
    if (!targetOrigin) return;

    window.parent.postMessage(message, targetOrigin);
    // eslint-disable-next-line no-console
    console.log('[Analytics] postMessage DESERTOURS_PAYMENT_READY (BED) envoyé', {
      payment_case_id: params.paymentCaseId,
      product: params.product,
      order_id: params.orderId,
    });
  } catch (error) {
    console.warn('[Analytics] Erreur postMessage BED PAYMENT_READY:', error);
  }
}

/**
 * Construit le message `DESERTOURS_BED_STATE`. Extrait de l'émission pour être
 * testable sans DOM ni iframe.
 */
export function buildBedStateMessage(params: {
  state: BedStateName;
  step?: number;
  crewNumber?: string | null;
  missingItems?: BedMissingItemPayload[];
  paymentCaseId?: string;
  bedToken?: string;
  orderId?: string;
  amountToPayCents?: number | null;
  /** Statut CRM après transition, tel que le serveur l'a renvoyé (00232). */
  bedStatus?: string | null;
  timestamp?: string;
}): BedStateMessage {
  const message: BedStateMessage = {
    type: 'DESERTOURS_BED_STATE',
    state: params.state,
    timestamp: params.timestamp ?? new Date().toISOString(),
  };

  // Enveloppe minimale : on n'ajoute une clé que si elle porte une information.
  // Le parent (Espace Participant) ne reçoit AUCUNE PII — juste des libellés de
  // champs manquants, le numéro d'équipage et les identifiants de dossier.
  if (params.step !== undefined) message.step = params.step;
  if (params.crewNumber) message.crew_number = params.crewNumber;
  if (params.missingItems) message.missing_items = params.missingItems;
  if (params.paymentCaseId) message.payment_case_id = params.paymentCaseId;
  if (params.bedToken) message.bed_token = params.bedToken;
  if (params.orderId) message.order_id = params.orderId;
  if (params.amountToPayCents !== undefined && params.amountToPayCents !== null) {
    message.amount_to_pay_cents = params.amountToPayCents;
  }
  if (params.bedStatus) message.bed_status = params.bedStatus;

  return message;
}

/**
 * Recette 4L du 22/09 (retour 31) : « après validation d'une étape, scroller
 * automatiquement en haut du formulaire afin d'éviter de se retrouver sur une
 * page blanche ».
 *
 * Sur /engagement, le site 4L rend le bulletin dans un cadre de HAUTEUR FIXE
 * (`md:h-[180rem] h-[130rem]`) : c'est la page hôte qui défile, pas le cadre.
 * Un `window.scrollTo(0)` à l'intérieur ne déplace rien de visible — d'où la
 * « page blanche » décrite par l'équipe quand un écran long est suivi d'un
 * écran court : le visiteur reste à l'ordonnée de l'ancien bas de page.
 *
 * Seule la page hôte peut corriger cela. On lui en donne le moyen : à chaque
 * changement d'étape, et à l'envoi du bulletin (l'écran « enregistré » remplace
 * le récapitulatif, le plus long), le bulletin demande à remonter — jamais à
 * l'ouverture. Message sans aucune donnée, origine cible explicite. Une page
 * hôte qui ne l'écoute pas n'en subit aucun effet. Contrat :
 * `docs/BED_IFRAME_EVENTS.md`.
 *
 * Réutilisé par le formulaire public en V2 (`?mode=iframe&v=2`) à l'écran
 * « Merci ! » : le cadre, qui suit le contenu, raccourcit sous le visiteur.
 */
export function notifyParentScrollToTop(): void {
  if (!isInIframe()) return;

  try {
    const targetOrigin = getTargetOrigin();
    if (!targetOrigin) return;

    window.parent.postMessage({ type: 'DESERTOURS_BED_SCROLL_TOP' as const }, targetOrigin);
  } catch (error) {
    console.warn('[Analytics] Erreur postMessage BED_SCROLL_TOP:', error);
  }
}

/**
 * Notifie le parent (site vitrine aujourd'hui, Espace Participant demain) d'une
 * transition du parcours BED : chargement, sauvegarde d'étape, soumission,
 * prêt-à-payer. Contrat complet : `docs/BED_IFRAME_EVENTS.md`.
 *
 * `payment_ready` s'AJOUTE à `DESERTOURS_PAYMENT_READY` (rétro-compatibilité) :
 * les deux messages sont émis, aucun consommateur existant n'est cassé.
 */
export function notifyParentBedState(params: {
  state: BedStateName;
  step?: number;
  crewNumber?: string | null;
  missingItems?: BedMissingItemPayload[];
  paymentCaseId?: string;
  bedToken?: string;
  orderId?: string;
  amountToPayCents?: number | null;
  bedStatus?: string | null;
}): void {
  if (!isInIframe()) {
    // eslint-disable-next-line no-console
    console.debug('[Analytics] Pas en iframe, postMessage BED_STATE ignoré', params.state);
    return;
  }

  try {
    const message = buildBedStateMessage(params);

    const targetOrigin = getTargetOrigin();
    if (!targetOrigin) return;

    window.parent.postMessage(message, targetOrigin);
    // eslint-disable-next-line no-console
    console.log('[Analytics] postMessage DESERTOURS_BED_STATE envoyé', {
      state: message.state,
      step: message.step,
      crew_number: message.crew_number,
      missing_items_count: message.missing_items?.length,
      payment_case_id: message.payment_case_id,
      bed_status: message.bed_status,
    });
  } catch (error) {
    console.warn('[Analytics] Erreur postMessage BED_STATE:', error);
  }
}

/**
 * Poignée de main V2 (`?mode=iframe&v=2`), étape 1 : le formulaire annonce au
 * site qu'il sait suivre sa hauteur. Le site répond par
 * `DESERTOURS_FORM_HEIGHT_REQUEST` (voir `isParentFormHeightRequest`) ; tant
 * qu'il ne l'a pas fait, le formulaire garde son rendu historique. Un site ou un
 * CRM sans V2, ou une origine mal configurée, laisse donc l'affichage actuel.
 * Message sans donnée.
 */
export function notifyParentFormReady(): void {
  if (!isInIframe()) return;

  try {
    const targetOrigin = getTargetOrigin();
    if (!targetOrigin) return;

    window.parent.postMessage({ type: 'DESERTOURS_FORM_READY' as const }, targetOrigin);
  } catch (error) {
    console.warn('[Analytics] Erreur postMessage FORM_READY:', error);
  }
}

/**
 * Poignée de main V2, étape 2 : vrai seulement pour une demande de hauteur
 * envoyée par la fenêtre parente, depuis l'origine `VITE_PARENT_ORIGIN`.
 * Toute autre fenêtre ou origine est ignorée.
 */
export function isParentFormHeightRequest(event: MessageEvent): boolean {
  if (!isInIframe()) return false;
  const parentOrigin = import.meta.env.VITE_PARENT_ORIGIN;
  if (!parentOrigin || event.origin !== parentOrigin) return false;
  if (event.source !== window.parent) return false;
  const data: unknown = event.data;
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { type?: unknown }).type === 'DESERTOURS_FORM_HEIGHT_REQUEST'
  );
}

/**
 * Notifie le parent de la hauteur réelle du formulaire responsive.
 * Le parent conserve la validation source + origin avant d'appliquer la valeur.
 */
export function notifyParentFormHeight(height: number): void {
  if (!isInIframe() || !Number.isFinite(height) || height <= 0) {
    return;
  }

  try {
    const targetOrigin = getTargetOrigin();
    if (!targetOrigin) return;

    const message: FormHeightMessage = {
      type: 'DESERTOURS_FORM_HEIGHT',
      height: Math.ceil(height),
    };

    window.parent.postMessage(message, targetOrigin);
  } catch (error) {
    console.warn('[Analytics] Erreur postMessage FORM_HEIGHT:', error);
  }
}
